import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { IconArrowBackUp, IconCheck, IconExclamationMark, IconX } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button } from './Button';
import { autoClose, drop, push, TOAST_MS, type ToastItem } from './toast-queue';
import s from './kit.module.css';

export interface ToastInput {
  msg: string;
  tone?: 'ok' | 'err';
  undo?: () => void;
}
type ToastFn = (t: ToastInput) => void;

const Ctx = createContext<ToastFn | null>(null);

/** Gọi toast từ màn: `toast({ msg: 'Đã đổi → Released', undo })`. */
export function useToast(): ToastFn {
  const f = useContext(Ctx);
  if (!f) throw new Error('useToast cần <ToastProvider> bọc ngoài');
  return f;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const close = useCallback((id: number) => setList((l) => drop(l, id)), []);
  const show = useCallback<ToastFn>((t) => {
    const id = ++seq.current;
    setList((l) => push(l, { id, msg: t.msg, tone: t.tone ?? 'ok', undo: t.undo }));
  }, []);
  const value = useMemo(() => show, [show]);
  // Esc / Ctrl+Z = Hoàn tác thông báo mới nhất có nút Hoàn tác (Mint-Motion mục 3)
  const latest = useRef<ToastItem | null>(null);
  latest.current = [...list].reverse().find((x) => x.undo) ?? null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = latest.current;
      if (!t?.undo || e.defaultPrevented) return;
      const undoKey = e.key === 'Escape' || ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z');
      if (!undoKey) return;
      const el = e.target as HTMLElement | null;
      if (e.key !== 'Escape' && el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
      e.preventDefault();
      t.undo();
      close(t.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className={s.toasts}>
        {list.map((t) => (
          <Toast key={t.id} t={t} close={close} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function Toast({ t, close }: { t: ToastItem; close: (id: number) => void }) {
  const tr = useT();
  const id = t.id;
  const onClose = useCallback(() => close(id), [close, id]);
  const [hold, setHold] = useState(false);
  useEffect(() => {
    if (!autoClose(t) || hold) return;
    const h = setTimeout(onClose, TOAST_MS);
    return () => clearTimeout(h);
  }, [t, hold, onClose]);
  const err = t.tone === 'err';
  return (
    <div
      className={`${s.toast} ${err ? s.toastErr : ''}`}
      role={err ? 'alert' : 'status'}
      onPointerEnter={() => setHold(true)}
      onPointerLeave={() => setHold(false)}
      onFocus={() => setHold(true)}
      onBlur={() => setHold(false)}
    >
      <span className={s.toastMark} aria-hidden="true">
        {err ? <IconExclamationMark size={13} stroke={3} /> : <IconCheck size={13} stroke={3} />}
      </span>
      <span className={s.toastMsg}>{t.msg}</span>
      {t.undo && (
        <Button
          variant="ask"
          size="sm"
          icon={<IconArrowBackUp size={16} aria-hidden="true" />}
          onClick={() => {
            t.undo?.();
            onClose();
          }}
        >
          {tr('common.undo')}
        </Button>
      )}
      {err && <Button variant="ghost" size="sm" aria-label={tr('ui.closeToast')} icon={<IconX size={16} aria-hidden="true" />} onClick={onClose} />}
    </div>
  );
}
